import { useEffect } from "react";

// ============================================================================
//  authrulesUi.jsx — primitivas compartidas (ConfiguracionCuentas + Bloqueos)
// ============================================================================

export function Alert({ kind = "error", children, onClose }) {
    if (!children) return null;
    const styles =
        kind === "error"
            ? "bg-rose-50 text-rose-700 border-rose-200"
            : kind === "ok"
                ? "bg-teal-50 text-teal-700 border-teal-200"
                : "bg-amber-50 text-amber-700 border-amber-200";
    return (
        <div className={`mb-3 flex items-start justify-between rounded-lg border px-3 py-2 text-sm ${styles}`}>
            <span>{children}</span>
            {onClose && (
                <button onClick={onClose} className="ml-3 shrink-0 opacity-60 hover:opacity-100">
                    ✕
                </button>
            )}
        </div>
    );
}

export function Badge({ children, tone = "slate" }) {
    const tones = {
        slate: "bg-slate-100 text-slate-600",
        teal: "bg-teal-100 text-teal-700",
        rose: "bg-rose-100 text-rose-700",
        amber: "bg-amber-100 text-amber-700",
    };
    return (
        <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${tones[tone]}`}>
            {children}
        </span>
    );
}

export function Field({ label, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">{label}</span>
            {children}
        </label>
    );
}

export function Input(props) {
    return (
        <input
            {...props}
            className={`w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 ${props.className || ""}`}
        />
    );
}

export function Select({ value, onChange, children, className = "", ...props }) {
    return (
        <select
            value={value}
            onChange={onChange}
            {...props}
            className={`w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm outline-none focus:border-teal-500 focus:ring-1 focus:ring-teal-500 ${className}`}
        >
            {children}
        </select>
    );
}

export function Btn({ variant = "primary", className = "", ...props }) {
    const v = {
        primary: "bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50",
        ghost: "bg-white text-slate-700 border border-slate-300 hover:bg-slate-50",
        danger: "bg-rose-600 text-white hover:bg-rose-700",
        subtle: "bg-slate-100 text-slate-700 hover:bg-slate-200",
    }[variant];
    return (
        <button
            {...props}
            className={`rounded-lg px-3 py-2 text-sm font-medium transition disabled:cursor-not-allowed ${v} ${className}`}
        />
    );
}

// Modal: cierra con (1) ESC, (2) clic en backdrop, (3) boton de cerrar.
export function Modal({ open, onClose, title, children, footer, wide }) {
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose]);

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose();
            }}
        >
            <div className={`w-full ${wide ? "max-w-3xl" : "max-w-lg"} overflow-hidden rounded-xl bg-white shadow-2xl`}>
                <div className="flex items-center justify-between border-b border-slate-200 px-5 py-3">
                    <h3 className="text-base font-semibold text-slate-800">{title}</h3>
                    <button
                        onClick={onClose}
                        className="grid h-7 w-7 place-items-center rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                        aria-label="Cerrar"
                    >
                        ✕
                    </button>
                </div>
                <div className="max-h-[70vh] overflow-y-auto px-5 py-4">{children}</div>
                {footer && (
                    <div className="flex justify-end gap-2 border-t border-slate-200 px-5 py-3">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}

export function Th({ children }) {
    return (
        <th className="border-b border-slate-200 px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
            {children}
        </th>
    );
}

export function Td({ children, mono }) {
    return (
        <td className={`border-b border-slate-100 px-3 py-2 text-sm text-slate-700 ${mono ? "font-mono text-xs" : ""}`}>
            {children}
        </td>
    );
}

export function Empty({ children }) {
    return (
        <div className="rounded-lg border border-dashed border-slate-300 px-4 py-10 text-center text-sm text-slate-400">
            {children}
        </div>
    );
}

export function Tabs({ tabs, active, onChange }) {
    return (
        <div className="mb-5 flex gap-1 border-b border-slate-200">
            {tabs.map((t) => (
                <button
                    key={t.id}
                    onClick={() => onChange(t.id)}
                    className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium transition ${active === t.id
                            ? "border-teal-600 text-teal-700"
                            : "border-transparent text-slate-500 hover:text-slate-700"
                        }`}
                >
                    {t.label}
                </button>
            ))}
        </div>
    );
}

// Quita claves vacias/null/undefined antes de mandar al backend.
export function limpia(obj) {
    const out = {};
    Object.keys(obj).forEach((k) => {
        const v = obj[k];
        if (v !== "" && v !== null && v !== undefined) out[k] = v;
    });
    return out;
}